import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, Plus, X, Building2 } from 'lucide-react'
import { ScoreComparison } from '@/components/lender/ScoreComparison'
import { BusinessSearchResults } from '@/components/lender/BusinessSearchResults'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/components/ui/MobileToast'
import { Business, TLBHScore } from '@/types'
import { businessService, BusinessListItem } from '@/services/businessService'

interface BusinessComparisonData {
  business: Business
  score: TLBHScore
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  trend: 'improving' | 'stable' | 'declining'
  alerts: number
}

// Helper function to convert API response to BusinessComparisonData
const convertApiToComparisonData = (apiData: BusinessListItem): BusinessComparisonData => {
  const score = parseFloat(apiData.score || '0')
  const band = apiData.band || 'E'
  
  // Determine risk level based on band
  let riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'medium'
  if (band === 'S' || band === 'A') riskLevel = 'low'
  else if (band === 'B' || band === 'C') riskLevel = 'medium'
  else if (band === 'D') riskLevel = 'high'
  else riskLevel = 'critical'
  
  // Parse address
  const addressParts = (apiData.registered_address || '').split(',').map(s => s.trim())
  
  return {
    business: {
      id: apiData.id,
      name: apiData.business_name,
      pan: apiData.pan,
      gstin: apiData.gstin || '',
      industry: apiData.industry_sector || 'Unknown',
      businessType: apiData.business_type as any,
      registrationDate: apiData.registration_date,
      contactEmail: apiData.contact_email || '',
      contactPhone: apiData.contact_phone || '',
      address: {
        street: addressParts[0] || '',
        city: addressParts[1] || '',
        state: addressParts[2] || '',
        pincode: addressParts[3] || '',
        country: 'India'
      },
      ownerId: '',
      createdAt: apiData.created_at,
      updatedAt: apiData.created_at
    },
    score: {
      id: apiData.id,
      businessId: apiData.id,
      tlbhScore: score,
      band: band,
      confidence: parseFloat(apiData.confidence || '0.85'),
      dimensionScores: {
        legal: { score: Number((score * 0.95).toFixed(2)), weight: 0.25, explanation: 'Legal compliance', verificationPoints: [], trend: 'stable', lastUpdated: apiData.calculated_at || new Date().toISOString() },
        financial: { score: Number((score * 1.02).toFixed(2)), weight: 0.30, explanation: 'Financial health', verificationPoints: [], trend: 'stable', lastUpdated: apiData.calculated_at || new Date().toISOString() },
        operational: { score: Number((score * 0.98).toFixed(2)), weight: 0.25, explanation: 'Operational efficiency', verificationPoints: [], trend: 'stable', lastUpdated: apiData.calculated_at || new Date().toISOString() },
        market: { score: Number((score * 1.01).toFixed(2)), weight: 0.20, explanation: 'Market presence', verificationPoints: [], trend: 'stable', lastUpdated: apiData.calculated_at || new Date().toISOString() }
      },
      redFlags: [],
      calculatedAt: apiData.calculated_at || new Date().toISOString(),
      calculationVersion: '1.0.0',
      dataSourcesUsed: ['MCA', 'GSTN'],
      validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    },
    riskLevel,
    trend: 'stable',
    alerts: 0
  }
}

export function ScoreComparisonPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedBusinesses, setSelectedBusinesses] = useState<BusinessComparisonData[]>([])
  const [showBusinessSearch, setShowBusinessSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<BusinessComparisonData[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [isLoadingInitial, setIsLoadingInitial] = useState(false)
  const showToast = useToast()

  // Load businesses from URL params on mount
  useEffect(() => {
    const loadBusinessesFromParams = async () => {
      const businessIds = searchParams.get('businesses')?.split(',') || []
      if (businessIds.length > 0) {
        setIsLoadingInitial(true)
        try {
          const businesses = await Promise.all(
            businessIds.map(async (id) => {
              try {
                const apiData = await businessService.getBusinessById(id)
                return convertApiToComparisonData(apiData)
              } catch (error) {
                console.error(`Failed to load business ${id}:`, error)
                return null
              }
            })
          )
          setSelectedBusinesses(businesses.filter(b => b !== null) as BusinessComparisonData[])
        } catch (error) {
          console.error('Failed to load businesses:', error)
          showToast.showToast({
            type: 'error',
            title: 'Load Failed',
            message: 'Unable to load businesses from URL.'
          })
        } finally {
          setIsLoadingInitial(false)
        }
      }
    }
    
    loadBusinessesFromParams()
  }, [searchParams])

  // Update URL when businesses change
  useEffect(() => {
    if (selectedBusinesses.length > 0) {
      const businessIds = selectedBusinesses.map(b => b.business.id).join(',')
      setSearchParams({ businesses: businessIds })
    } else {
      setSearchParams({})
    }
  }, [selectedBusinesses, setSearchParams])

  const handleAddBusiness = () => {
    if (selectedBusinesses.length >= 4) {
      showToast.showToast({
        type: 'warning',
        title: 'Maximum Reached',
        message: 'You can compare up to 4 businesses at a time.'
      })
      return
    }
    setShowBusinessSearch(true)
  }

  const handleRemoveBusiness = (businessId: string) => {
    setSelectedBusinesses(prev => prev.filter(b => b.business.id !== businessId))
  }

  const handleSelectBusiness = (business: BusinessComparisonData) => {
    if (selectedBusinesses.find(b => b.business.id === business.business.id)) {
      showToast.showToast({
        type: 'warning',
        title: 'Already Added',
        message: 'This business is already in the comparison.'
      })
      return
    }

    if (selectedBusinesses.length >= 4) {
      showToast.showToast({
        type: 'warning',
        title: 'Maximum Reached',
        message: 'You can compare up to 4 businesses at a time.'
      })
      return
    }

    setSelectedBusinesses(prev => [...prev, business])
    setShowBusinessSearch(false)
    setSearchQuery('')
    setSearchResults([])
  }

  const handleSearch = async () => {
    setIsSearching(true)
    try {
      const response = await businessService.getBusinesses({
        search: searchQuery.trim() || undefined,
        limit: 20
      })
      
      const converted = response.businesses.map(convertApiToComparisonData)
      setSearchResults(converted)
    } catch (error) {
      console.error('Search failed:', error)
      showToast.showToast({
        type: 'error',
        title: 'Search Failed',
        message: 'Unable to search businesses. Please try again.'
      })
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  // Show search results when search is active
  useEffect(() => {
    if (showBusinessSearch) {
      handleSearch()
    }
  }, [showBusinessSearch])

  return (
    <div className="space-y-6">
      {/* Business Search Modal */}
      {showBusinessSearch && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75"
              onClick={() => setShowBusinessSearch(false)}
            />

            {/* Modal panel */}
            <div className="inline-block w-full max-w-4xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-xl rounded-lg">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Add Business to Comparison
                </h2>
                <button
                  onClick={() => setShowBusinessSearch(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Search */}
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex space-x-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        placeholder="Search by business name, PAN, or GSTIN..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyPress={handleKeyPress}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>
                  </div>
                  <button
                    onClick={handleSearch}
                    disabled={isSearching}
                    className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium disabled:opacity-50"
                  >
                    {isSearching ? <LoadingSpinner size="sm" /> : 'Search'}
                  </button>
                </div>
              </div>

              {/* Results */}
              <div className="px-6 py-4 max-h-96 overflow-y-auto">
                {searchResults.length > 0 ? (
                  <div className="space-y-3">
                    {searchResults.map((business) => (
                      <div
                        key={business.business.id}
                        className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                        onClick={() => handleSelectBusiness(business)}
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900 dark:text-white">
                              {business.business.name}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {business.business.industry} • Score: {business.score.tlbhScore.toFixed(2)} ({business.score.band})
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSelectBusiness(business)
                          }}
                          className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">
                      {searchQuery ? 'No businesses found matching your search.' : 'Start typing to search for businesses.'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      {selectedBusinesses.length > 0 ? (
        <ScoreComparison
          businesses={selectedBusinesses}
          onAddBusiness={handleAddBusiness}
          onRemoveBusiness={handleRemoveBusiness}
        />
      ) : (
        <div className="text-center py-12">
          <div className="max-w-md mx-auto">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Compare Business Scores
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Add businesses to compare their TLBH scores, risk levels, and dimensional breakdowns side by side.
            </p>
            <button
              onClick={handleAddBusiness}
              className="inline-flex items-center px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add First Business
            </button>
          </div>
        </div>
      )}
    </div>
  )
}